/* eslint-disable import/no-extraneous-dependencies */
import * as React from 'react';
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, drawSelection, dropCursor, highlightActiveLine, highlightActiveLineGutter, highlightSpecialChars, crosshairCursor, rectangularSelection } from '@codemirror/view';
import { defaultKeymap, indentWithTab, history, historyKeymap } from '@codemirror/commands';
import { indentOnInput, bracketMatching, foldGutter } from '@codemirror/language';
import { xml } from '@codemirror/lang-xml';
import styleNames from '@pareto-engineering/bem/exports';
import { theme } from "./common";
import "./styles.scss";
const baseClassName = styleNames.base;
const componentClassName = 'code-editor';

/**
 * codemirror documentation:
 * https://codemirror.net/
 */

const XMLEditor = ({
  id,
  className: userClassName,
  style,
  readOnly,
  height,
  gutterWidth,
  config,
  onChange,
  onBlur
}) => {
  const editorRef = useRef();
  useEffect(() => {
    const startState = EditorState.create({
      doc: config,
      extensions: [keymap.of([defaultKeymap, indentWithTab, ...historyKeymap]), indentOnInput(), lineNumbers(), bracketMatching(), foldGutter(), drawSelection(), highlightActiveLine(), highlightActiveLineGutter(), highlightSpecialChars(), dropCursor(), rectangularSelection(), crosshairCursor(), xml(), history(), theme, EditorState.readOnly.of(readOnly), EditorView.updateListener.of(view => {
        onChange(view);
        // view.state.doc.toString() to receive the current content in the editor.
      }), EditorView.focusChangeEffect.of((state, focused) => {
        if (!focused) {
          onBlur?.(state);
        }
      }), EditorView.domEventHandlers({
        keydown(e) {
          e.stopPropagation();
        }
      })]
    });
    const view = new EditorView({
      state: startState,
      parent: editorRef.current
    });
    return () => {
      view.destroy();
    };
  }, [editorRef]);
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    ref: editorRef,
    className: [baseClassName, componentClassName, userClassName].filter(e => e).join(' '),
    style: {
      '--height': height,
      '--gutter-width': gutterWidth,
      ...style
    }
  });
};
XMLEditor.propTypes = {
  /**
     * The HTML id for this element
     */
  id: PropTypes.string,
  /**
     * The HTML class names for this element
     */
  className: PropTypes.string,
  /**
     * The React-written, css properties for this element.
     */
  style: PropTypes.objectOf(PropTypes.string),
  /**
     *  Whether or not the content is read only.
     */
  readOnly: PropTypes.bool,
  /**
   * The initial labeling configuration.
   */
  config: PropTypes.string,
  /**
   * The height of the editor.
   */
  height: PropTypes.string,
  /**
   * The callback for when the labeling configuration changes.
   */
  onChange: PropTypes.func,
  /**
   * The width of the gutter.
   */
  gutterWidth: PropTypes.string,
  /**
   * The callback for when the editor loses focus.
   */
  onBlur: PropTypes.func
};
XMLEditor.defaultProps = {
  config: `<View>
  <!--Edit this config to customize the labeling interface.-->
</View>
  `,
  height: '20em',
  gutterWidth: '3em'
};
export default XMLEditor;